import { Box, Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import { BundlePollQuestion, PollQuestion, RangePollQuestion, ResponseValue, SelectPollQuestion } from '../../types';
import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';

export interface QuestionFormProps {
  onSubmitQuestion: (question: PollQuestion) => void;
  onCancel(): void;
  editingQuestion?: PollQuestion;
}

const QuestionForm: React.FC<QuestionFormProps> = ({ onSubmitQuestion, onCancel, editingQuestion }) => {
  const [question, setQuestion] = useState(editingQuestion?.text ?? '');
  const [type, setType] = useState<PollQuestion['type']>(editingQuestion?.type ?? 'select');
  const [options, setOptions] = useState<string[]>(editingQuestion?.type === 'select' || editingQuestion?.type === 'multiselect' ? (editingQuestion as SelectPollQuestion).options : []);
  const [controlType, setControlType] = useState<SelectPollQuestion['controlType']>(editingQuestion?.type === 'select' ? (editingQuestion as SelectPollQuestion).controlType : 'radio');
  const [min, setMin] = useState(editingQuestion?.type === 'range' ? (editingQuestion as RangePollQuestion).min : 0);
  const [max, setMax] = useState(editingQuestion?.type === 'range' ? (editingQuestion as RangePollQuestion).max : 10);
  const [selectFromListOnly, setSelectFromListOnly] = useState(editingQuestion?.type === 'voter' ? editingQuestion.selectFromListOnly : false);
  const [checkHasResponded, setCheckHasResponded] = useState(editingQuestion?.type === 'voter' ? editingQuestion.checkHasResponded : false);
  const [voterMunicipality, setVoterMunicipality] = useState(editingQuestion?.type === 'voter' ? editingQuestion.voterMunicipality : undefined);
  const [voterProvince, setVoterProvince] = useState(editingQuestion?.type === 'voter' ? editingQuestion.voterProvince : undefined);
  
  // Bundle question specific states
  const [bundleOptions, setBundleOptions] = useState<{[option: string]: {[question: string]: ResponseValue}}>
    (editingQuestion?.type === 'bundle' ? (editingQuestion as BundlePollQuestion).qnas : {});
  const [newOptionName, setNewOptionName] = useState('');
  const [newQuestionForOption, setNewQuestionForOption] = useState('');
  const [newQuestionValue, setNewQuestionValue] = useState<ResponseValue>('');

  const resetForm = () => {
    setQuestion('');
    setType('select');
    setOptions([]);
    setControlType('radio');
    setMin(0);
    setMax(10);
    setSelectFromListOnly(false);
    setCheckHasResponded(false);
    setBundleOptions({});
    setNewOptionName('');
    setNewQuestionForOption('');
    setNewQuestionValue('');
  };

  const addBundleOption = () => {
    if (newOptionName.trim()) {
      setBundleOptions(prev => ({
        ...prev,
        [newOptionName.trim()]: {}
      }));
      setNewOptionName('');
    }
  };

  const addQuestionToOption = (option: string) => {
    if (newQuestionForOption.trim() && newQuestionValue !== null) {
      setBundleOptions(prev => ({
        ...prev,
        [option]: {
          ...prev[option],
          [newQuestionForOption.trim()]: newQuestionValue
        }
      }));
      setNewQuestionForOption('');
      setNewQuestionValue('');
    }
  };

  const removeOption = (option: string) => {
    const updatedOptions = {...bundleOptions};
    delete updatedOptions[option];
    setBundleOptions(updatedOptions);
  };

  const removeQuestionFromOption = (option: string, questionToRemove: string) => {
    const updatedOptions = {...bundleOptions};
    delete updatedOptions[option][questionToRemove];
    setBundleOptions(updatedOptions);
  };

  const handleQuestionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (question && type) {
      let submittedQuestion: PollQuestion;
      switch (type) {
        case 'select':
          submittedQuestion = {
            id: editingQuestion?.id ?? uuidv4(),
            text: question,
            type: type,
            options: options,
            controlType: controlType
          };
          break;
        case 'multiselect':
          submittedQuestion = {
            id: editingQuestion?.id ?? uuidv4(),
            text: question,
            type: type,
            options: options,
            controlType: "checkbox"
          };
          break;
        case 'range':
          submittedQuestion = {
            id: editingQuestion?.id ?? uuidv4(),
            text: question,
            type: type,
            min: min,
            max: max
          };
          break;
        case 'voter':
          submittedQuestion = {
            id: editingQuestion?.id ?? uuidv4(),
            text: question,
            type: type,
            selectFromListOnly: selectFromListOnly,
            checkHasResponded: checkHasResponded,
            voterMunicipality: voterMunicipality,
            voterProvince: voterProvince
          };
          break;
        case 'text':
          submittedQuestion = {
            id: editingQuestion?.id ?? uuidv4(),
            text: question,
            type: type
          };
          break;
        case 'bundle':
          submittedQuestion = {
            id: editingQuestion?.id ?? uuidv4(),
            text: question,
            type: type,
            qnas: bundleOptions
          };
          break;
      }
      onSubmitQuestion(submittedQuestion);
      if (!editingQuestion) {
        // Reset form if adding a new question
        resetForm();
      }
    }
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '95%', margin: 'auto',  border: '1px solid #ccc', padding: 2, borderRadius: 2 }}>
      <TextField
        label="Question"
        variant="outlined"
        fullWidth
        required
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
      />
      <TextField
        select
        label="Type"
        variant="outlined"
        fullWidth
        required
        value={type}
        onChange={(e) => setType(e.target.value as PollQuestion['type'])}
      >
        <MenuItem value="select">Select</MenuItem>
        <MenuItem value="multiselect">Multiselect</MenuItem>
        <MenuItem value="range">Range</MenuItem>
        <MenuItem value="text">Text</MenuItem>
        <MenuItem value="voter">Voter Select</MenuItem>
        <MenuItem value="bundle">Bundle</MenuItem>
      </TextField>
      {type === 'select' && (
        <> 
          <TextField
            label="Options (comma-separated)"
            variant="outlined"
            fullWidth
            value={options.join(', ')}
            onChange={(e) => setOptions(e.target.value.split(',').map(option => option.trim()))}
          />
          <TextField
            select
            label="Control Type"
            variant="outlined"
            fullWidth
            value={controlType}
            onChange={(e) => setControlType(e.target.value as SelectPollQuestion['controlType'])}
          >
            <MenuItem value="radio">Radio</MenuItem>
            <MenuItem value="dropdown">Dropdown</MenuItem>
          </TextField>
        </>
      )}
      {type === 'multiselect' && (
        <TextField
          label="Options (comma-separated)"
          variant="outlined"
          fullWidth
          value={options.join(', ')}
          onChange={(e) => setOptions(e.target.value.split(',').map(option => option.trim()))}
        />
      )}
      {type === 'range' && (
        <> 
          <TextField
            label="Minimum Value"
            type="number"
            variant="outlined"
            fullWidth
            value={min}
            onChange={(e) => setMin(Number(e.target.value))}
          />
          <TextField
            label="Maximum Value"
            type="number"
            variant="outlined"
            fullWidth
            value={max}
            onChange={(e) => setMax(Number(e.target.value))}
          />
        </>
      )}
      {type === 'voter' && (
        <TextField
          select
          label="Check if voter should be from voters list only"
          variant="outlined"
          fullWidth
          value={selectFromListOnly}
          onChange={(e) => setSelectFromListOnly(e.target.value === "true")}
        >
          <MenuItem value="true">True</MenuItem>
          <MenuItem value="false">False</MenuItem>
        </TextField>
      )}
      {type === 'voter' && (
        <TextField
          select
          label="Check if user has responded"
          variant="outlined"
          fullWidth
          value={checkHasResponded}
          onChange={(e) => setCheckHasResponded(e.target.value === "true")}
        >
          <MenuItem value="true">True</MenuItem>
          <MenuItem value="false">False</MenuItem>
        </TextField>
      )}
      {type === 'voter' && (
        <TextField
          label="Limit to city/municipality"
          variant="outlined"
          fullWidth
          value={voterMunicipality}
          onChange={(e) => setVoterMunicipality(e.target.value)}
        />
      )}
      {type === 'voter' && (
        <TextField
          label="Limit to province"
          variant="outlined"
          fullWidth
          value={voterProvince}
          onChange={(e) => setVoterProvince(e.target.value)}
        />
      )}
      {type === 'bundle' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
            <TextField
              label="New Option Name"
              variant="outlined"
              fullWidth
              value={newOptionName}
              onChange={(e) => setNewOptionName(e.target.value)}
            />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={addBundleOption}
              disabled={!newOptionName.trim()}
            >
              Add Option
            </Button>
          </Box>

          {Object.entries(bundleOptions).map(([option, questions]) => (
            <Box key={option} sx={{ border: '1px solid #ccc', padding: 2, marginBottom: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <Typography variant="h6">{option}</Typography>
                <IconButton onClick={() => removeOption(option)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>

              {Object.entries(questions).map(([q, value]) => (
                <Box key={q} sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1 }}>
                  <Typography>{q}: {value?.toString()}</Typography>
                  <IconButton onClick={() => removeQuestionFromOption(option, q)} color="error" size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
                <TextField
                  label="Question"
                  variant="outlined"
                  fullWidth
                  value={newQuestionForOption}
                  onChange={(e) => setNewQuestionForOption(e.target.value)}
                />
                <TextField
                  label="Value"
                  variant="outlined"
                  fullWidth
                  value={newQuestionValue ?? ''}
                  onChange={(e) => setNewQuestionValue(e.target.value || null)}
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => addQuestionToOption(option)}
                  disabled={!newQuestionForOption.trim()}
                >
                  Add Question
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleQuestionSubmit}>
          {editingQuestion ? 'Update Question' : 'Add Question'}
        </Button>
        <Button variant="outlined" onClick={() => handleCancel()}>
          Cancel
        </Button>
      </Box>
    </Box>
  )
}

export default QuestionForm;
